import type { JSX } from 'react'

import {
  comparePrescriptions,
  dioptre,
  formatDioptre,
  type CompareEye,
  type CompareField,
  type Dioptre,
  type FieldChange,
  type HorizontalPrism,
  type ObliquePrism,
  type VerticalPrism,
} from '@lensmira/domain'

import { Ltr, useTranslation, type Translation } from '../i18n.js'
import { sentence } from '../sentence.js'

import type { PrescriptionEyeView, PrescriptionVersionView } from './CustomerDetail.js'

/**
 * `RXW-006`: the current version of a prescription against the one it replaced, one delta per field.
 *
 * Read-only (`RX-002`). It is computed from the two versions the customer read already carries, so
 * `BR-03` holds without a second endpoint: no `prescription_view`, no versions, nothing to compare.
 *
 * Every cell says what happened in words as well as numbers, so a change is never carried by colour
 * alone. Figures sit in `<Ltr>` and the grid is `dir="ltr"` like the version tables beside it.
 * Every parameter of the correction the record stores is compared. Visual acuity and notes are not,
 * and a line under the table says so, so "No change" never covers a field that was not looked at.
 */

type T = Translation['t']

const FIELDS: readonly CompareField[] = [
  'sphere',
  'cylinder',
  'axis',
  'add',
  'intermediateAdd',
  'prismHorizontal',
  'prismVertical',
  'prismOblique',
  'backVertexDistance',
  'balanceLens',
]

const FIELD_LABEL = {
  sphere: 'rx.col.sphere',
  cylinder: 'rx.col.cylinder',
  axis: 'rx.col.axis',
  add: 'rx.col.add',
  intermediateAdd: 'customerDetail.rx.compareField.intermediateAdd',
  prismHorizontal: 'customerDetail.rx.compareField.prismHorizontal',
  prismVertical: 'customerDetail.rx.compareField.prismVertical',
  prismOblique: 'customerDetail.rx.compareField.prismOblique',
  backVertexDistance: 'customerDetail.rx.compareField.backVertexDistance',
  balanceLens: 'customerDetail.rx.compareField.balanceLens',
} as const

/** A prism with no base, or one the registry does not know, is not guessed: the throw lands in the
 *  caller's "cannot be shown" branch (Appendix I requires the base, and the table enforces it). */
function baseOf<B extends string>(value: string | undefined, allowed: readonly B[]): B {
  const found = allowed.find((b) => b === value)
  if (found === undefined) throw new Error(`prism base ${String(value)} is not recognised`)
  return found
}

function requiredDegrees(value: number | undefined): number {
  if (value === undefined) throw new Error('oblique prism has no base angle')
  return value
}

function toCompareEye(eye: PrescriptionEyeView): CompareEye {
  return {
    sphere: dioptre(eye.sphere),
    cylinder: dioptre(eye.cylinder),
    ...(eye.axis === undefined ? {} : { axis: eye.axis }),
    ...(eye.add === undefined ? {} : { add: dioptre(eye.add) }),
    ...(eye.intermediateAdd === undefined ? {} : { intermediateAdd: dioptre(eye.intermediateAdd) }),
    ...(eye.prismHorizontal === undefined
      ? {}
      : {
          prismHorizontal: {
            magnitude: dioptre(eye.prismHorizontal),
            base: baseOf(eye.prismBaseHorizontal, ['in', 'out']),
          } satisfies HorizontalPrism,
        }),
    ...(eye.prismVertical === undefined
      ? {}
      : {
          prismVertical: {
            magnitude: dioptre(eye.prismVertical),
            base: baseOf(eye.prismBaseVertical, ['up', 'down']),
          } satisfies VerticalPrism,
        }),
    ...(eye.prismOblique === undefined
      ? {}
      : {
          prismOblique: {
            magnitude: dioptre(eye.prismOblique),
            baseDegrees: requiredDegrees(eye.prismObliqueDegrees),
          } satisfies ObliquePrism,
        }),
    ...(eye.backVertexDistanceMm === undefined
      ? {}
      : { backVertexDistance: eye.backVertexDistanceMm }),
    isBalanceLens: eye.isBalanceLens === true,
  }
}

function signed(n: number): string {
  return `${n < 0 ? '-' : '+'}${Math.abs(n)}`
}

/** A prism reads as its magnitude and its base together: a base on its own is the wrong direction. */
function figure(
  t: T,
  field: CompareField,
  value: number | undefined,
  base: string | number | undefined,
): string {
  if (value === undefined) return ''
  switch (field) {
    case 'axis':
      return String(value)
    case 'backVertexDistance':
      return `${value} mm`
    case 'balanceLens':
      return t(
        value === 1 ? 'customerDetail.rx.compareBalanceYes' : 'customerDetail.rx.compareBalanceNo',
      )
    case 'prismOblique':
      return `${formatDioptre(value as Dioptre)} @ ${base ?? ''}`
    case 'prismHorizontal':
    case 'prismVertical':
      return `${formatDioptre(value as Dioptre)} ${base ?? ''}`
    default:
      return formatDioptre(value as Dioptre)
  }
}

function cell(t: T, c: FieldChange): JSX.Element {
  const before = <Ltr>{figure(t, c.field, c.before, c.beforeBase)}</Ltr>
  const after = <Ltr>{figure(t, c.field, c.after, c.afterBase)}</Ltr>
  switch (c.kind) {
    case 'unchanged':
      return c.after === undefined ? (
        <>{t('common.notRecorded')}</>
      ) : (
        sentence(t, 'customerDetail.rx.compareUnchanged', { value: after })
      )
    case 'added':
      return sentence(t, 'customerDetail.rx.compareAdded', { after })
    case 'removed':
      return sentence(t, 'customerDetail.rx.compareRemoved', { before })
    case 'changed': {
      const delta =
        c.deltaQuarters !== undefined
          ? formatDioptre(c.deltaQuarters as Dioptre)
          : c.deltaDegrees !== undefined
            ? signed(c.deltaDegrees)
            : c.deltaMillimetres !== undefined
              ? `${signed(c.deltaMillimetres)} mm`
              : undefined
      if (delta === undefined) {
        return sentence(t, 'customerDetail.rx.compareChangedNoDelta', { before, after })
      }
      return sentence(t, 'customerDetail.rx.compareChanged', {
        before,
        after,
        delta: <Ltr>{delta}</Ltr>,
      })
    }
  }
}

export function RxComparison({
  previous,
  current,
}: {
  readonly previous: PrescriptionVersionView
  readonly current: PrescriptionVersionView
}): JSX.Element {
  const { t } = useTranslation()

  // `dioptre()` throws on a value that is not a 0.25 step. One corrupt row must not blank the
  // customer screen, so the comparison says it cannot be shown and the version tables stay.
  let changes: readonly FieldChange[]
  try {
    changes = comparePrescriptions(
      { od: toCompareEye(previous.od), os: toCompareEye(previous.os) },
      { od: toCompareEye(current.od), os: toCompareEye(current.os) },
    )
  } catch {
    return <p className="lm-hint">{t('customerDetail.rx.compareUnreadable')}</p>
  }

  const find = (eye: 'od' | 'os', field: CompareField): FieldChange | undefined =>
    changes.find((c) => c.eye === eye && c.field === field)

  return (
    <div className="lm-rx-compare__body">
      <p className="lm-rx-compare__heading">
        {t('customerDetail.rx.compareHeading', {
          previous: previous.version,
          current: current.version,
        })}
      </p>
      <table dir="ltr">
        <thead>
          <tr>
            <th scope="col">{t('customerDetail.rx.compareFieldColumn')}</th>
            <th scope="col">{t('customerDetail.rx.row.right')}</th>
            <th scope="col">{t('customerDetail.rx.row.left')}</th>
          </tr>
        </thead>
        <tbody>
          {FIELDS.map((field) => {
            const od = find('od', field)
            const os = find('os', field)
            return (
              <tr key={field}>
                <th scope="row">{t(FIELD_LABEL[field])}</th>
                <td>{od === undefined ? null : cell(t, od)}</td>
                <td>{os === undefined ? null : cell(t, os)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="lm-hint">{t('customerDetail.rx.compareNotCompared')}</p>
    </div>
  )
}
